import { AfterContentChecked, AfterViewInit, ChangeDetectorRef, Component, ElementRef, EventEmitter, forwardRef, HostListener, Input, OnChanges, OnInit, Output, Renderer2, ViewChild } from '@angular/core';
import { ControlValueAccessor, FormControl, NG_VALIDATORS, NG_VALUE_ACCESSOR } from '@angular/forms';
import { BrowserDetectUtil } from 'sharedGuiSmall/utils/browser-detect-util';
import { DomSmallUtils } from 'sharedGuiSmall/utils/dom-utils';
import { Utils } from 'sharedGuiSmall/utils/utils';
import { keyPressServiceKeys } from './keyPressServiceKeys';

const CUSTOM_INPUT_CONTROL_VALUE_ACCESSOR: any = {
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => ImageTextComponent),
    multi: true
};

@Component({
    selector: 'img-text',
    templateUrl: './image-text.component.html',
    styleUrls: ['./image-text.component.less'],
    providers: [CUSTOM_INPUT_CONTROL_VALUE_ACCESSOR,
        {
            provide: NG_VALIDATORS,
            useExisting: forwardRef(() => ImageTextComponent),
            multi: true
        }
    ]
})
export class ImageTextComponent implements ControlValueAccessor, OnInit, OnChanges, AfterViewInit, AfterContentChecked {

    notValidEmailText = $localize`Not a valid email`;
    requiredFieldText = $localize`Required field`;

    @Input()
    public hint: string = $localize`ENTER_AMOUNT`;

    @Input()
    label: string = 'label';

    @Input()
    height: number = 20;

    @Input()
    width: number = 100;

    @Input()
    backgroundColor: string = '';

    @Input()
    fieldClass: string = 'small-field';

    @Input()
    showImg: boolean = false;

    @Input()
    isPassword: boolean = false;

    @Input()
    isPhone: boolean = false;

    @Input()
    isNumber: boolean = false;

    @Input()
    valueType: string = 'text';

    @Input()
    public precision: number = 2;

    @Input()
    public minValue: number = 0;

    @Input()
    public maxValue: number = Number.MAX_SAFE_INTEGER;

    isImgShowing: boolean = false;

    @Input()
    inputMargin: number = 0;

    @Input()
    getDisplayValue!: Function;

    @ViewChild('topLabel')
    topLabel!: ElementRef<HTMLDivElement>;

    @ViewChild('topSpace')
    topSpace!: ElementRef<HTMLDivElement>;

    @ViewChild('inputArea')
    inputAreaElement!: ElementRef<HTMLDivElement>;

    @ViewChild('input')
    inputElement!: ElementRef<HTMLInputElement>;

    @ViewChild('inputCover')
    private inputCover!: ElementRef<HTMLInputElement>;

    @ViewChild('main')
    mainElement!: ElementRef<HTMLDivElement>;

    @ViewChild('firstTd')
    firstTd!: ElementRef<HTMLTableCellElement>;

    private firstTdWidth: number = 0;

    @ViewChild('lastTd')
    lastTd!: ElementRef<HTMLTableCellElement>;

    @Output() changeCaller: EventEmitter<any> = new EventEmitter();

    @Output() inputEv: EventEmitter<any> = new EventEmitter();

    private _value: string = '';

    inputWidth: number = 0;

    private regexPhone: any = /^[0-9+-\s]*$/;
    private regexNumber: any = /^[0-9\s]*$/;

    private regexNormal: any = /^(\d)*(\.(\d)*)?$/;
    private regexNegative: any = /^(\-)?(\d)*(\.(\d)*)?$/;

    private regex = this.regexNormal;

    private regexNormalKeyPress: any = /^\d$/;
    private regexNegativeKeyPress: any = /^\-|\d$/;

    private regexKeyPress = this.regexNormalKeyPress;

    isErrorShowing: boolean = false;
    error: string = '';

    private c: FormControl<string> | undefined;

    @Input()
    maxlen: number | undefined;  //input becomes broken if set to 0, related to html attribute

    private focusTime: number = 0;

    @Input()
    disabled: boolean = false;

    @Input()
    testId: string = "";

    isEditing: boolean = false;

    private prevValue: string = '';

    @Input()
    labelClass: string = "lab";

    constructor(private element: ElementRef,
        private renderer: Renderer2,
        private chDet: ChangeDetectorRef) {
    }

    @HostListener('paste', ['$event'])
    handlePasteEvent(evt: ClipboardEvent): void {
        // Extract number from formated value (1,000.12$ -> 1000.12)
        if (this.valueType === 'number') {
            evt.stopPropagation();
            evt.preventDefault();

            const clipboardData = evt['clipboardData'] || window['clipboardData'];
            let pasteData = clipboardData.getData(BrowserDetectUtil.isIe() ? 'text' : 'text/plain') as string;

            // Remove thousand separator when copy from Excel
            let re = new RegExp(`\\${Utils.thousandSeparator}`, 'g');
            if (Utils.thousandSeparator.length &&
                (Utils.thousandSeparator.charCodeAt(0) === 32 ||
                    Utils.thousandSeparator.charCodeAt(0) === 160))
                pasteData = pasteData.replace(/ /g, '');
            else
                pasteData = pasteData.replace(re, '');

            // Replace local decimal separator with point
            re = new RegExp(`\\${Utils.decimalSeparator}`, 'g');
            pasteData = pasteData.replace(re, '.');

            // Remove all except digits and decimal separator
            pasteData = !this.minValue
                ? pasteData.replace(/[^0-9,.]/g, '')
                : pasteData.replace(/[^0-9,.-]/g, '');

            this.value = pasteData;

            this.inputEv.emit(this.value);
        }
    }

    ngOnInit(): void {

        this.handleChange();
        //this.renderer.setAttribute(this.inputElement.nativeElement, "readonly", "true");
    }

    ngOnChanges(): void {

        if (this.minValue < 0) {

            this.regex = this.regexNegative;
            this.regexKeyPress = this.regexNegativeKeyPress;
        }
        else {

            this.regex = this.regexNormal;
            this.regexKeyPress = this.regexNormalKeyPress;
        }
    }

    ngAfterContentChecked() {
        if (this.visibleOnPage && getComputedStyle(this.inputElement.nativeElement).width == "0px")
            this.handleChange();
    }

    get visibleOnPage() {
        const el = this.mainElement.nativeElement;
        return el.offsetHeight != 0 && el.offsetWidth != 0 && getComputedStyle(el).display != 'none';
    }

    getEl(): HTMLElement {

        return this.element.nativeElement;
    }

    ngAfterViewInit(): void {

        if (this.inputMargin) {
            this.renderer.setStyle(this.inputElement.nativeElement, "margin-left", this.inputMargin + 'px');
            this.renderer.setStyle(this.inputCover.nativeElement, "margin-left", this.inputMargin + 'px');
        }

        let topLabelHeight = DomSmallUtils.height(this.topLabel?.nativeElement);
        let topSpaceHeight = DomSmallUtils.height(this.topSpace?.nativeElement);

        this.renderer.setStyle(this.inputAreaElement.nativeElement, "height", (this.height - topLabelHeight - topSpaceHeight) + 'px');
        if (this.width > 0) this.renderer.setStyle(this.inputAreaElement.nativeElement, "width", this.width + 'px');
        this.renderer.setStyle(this.inputElement.nativeElement, "height", (this.height - topLabelHeight - topSpaceHeight) + 'px');
        this.renderer.setStyle(this.inputCover.nativeElement, "height", (this.height - topLabelHeight - topSpaceHeight) + 'px');
        this.renderer.setStyle(this.mainElement.nativeElement, "height", this.height + 'px');
        if (this.width > 0) this.renderer.setStyle(this.mainElement.nativeElement, "width", this.width + 'px');

        if (this.width > 0) this.renderer.setStyle(this.element.nativeElement, "width", this.width + 'px');
        this.renderer.setStyle(this.element.nativeElement, "height", this.height + 'px');
    }


    onChangeEv(obj: any): void {

        if (this.disabled) return;

        this.changeCaller.emit(obj.target.value);
        const isEmpty = !Utils.isValue(this._value) || Utils.isEmpty(this._value);
        this.isImgShowing = isEmpty ? this.showImg : false;
    }

    handleChange(): void {

        //if (this.disabled) return;

        const isEmpty = !Utils.isValue(this._value) || Utils.isEmpty(this._value.toString());
        this.isImgShowing = isEmpty ? this.showImg : false;
        this.chDet.detectChanges();
        if (this.firstTd) this.firstTdWidth = this.firstTd.nativeElement.offsetWidth;
        if (!this.firstTdWidth) this.firstTdWidth = 0;
        let lastTdWidth = 0;
        if (this.lastTd) lastTdWidth = this.lastTd.nativeElement.offsetWidth;
        if (this.width > 0) this.inputWidth = this.width - (this.isImgShowing ? this.firstTdWidth : 0) - lastTdWidth - parseInt(getComputedStyle(this.inputElement.nativeElement).getPropertyValue('margin-left')) - 2;

    }

    updateError(c: FormControl<string>): void {

        if (this.disabled) return;
        this.isErrorShowing = c.invalid;
        this.chDet.detectChanges();
        let firstTdWidth = 0;
        let lastTdWidth = 0;
        if (this.firstTd) firstTdWidth = this.firstTd.nativeElement.offsetWidth;
        if (this.lastTd) lastTdWidth = this.lastTd.nativeElement.offsetWidth;
        if (this.width > 0) this.inputWidth = this.width - firstTdWidth - lastTdWidth - parseInt(getComputedStyle(this.inputElement.nativeElement).getPropertyValue('margin-left')) - 2;

        this.error = '';
        if (this.isErrorShowing && c.errors?.emailornull) this.error = this.notValidEmailText;
        if (this.isErrorShowing && c.errors?.required) this.error = this.requiredFieldText;

        this.renderer.setStyle(this.element.nativeElement, "height", (this.height + (this.isErrorShowing ? 16 : 0)) + 'px');
        this.renderer.setStyle(this.mainElement.nativeElement, "height", (this.height + (this.isErrorShowing ? 16 : 0)) + 'px');
    }

    onInput(): void {

        if (this.disabled) return;

        this.inputEv.emit(this.value);
    }

    onFocus(): void {

        if (this.disabled) return;

        //this.renderer.removeAttribute(this.inputElement.nativeElement, "readonly");

        if (!BrowserDetectUtil.isSafari()) this.inputElement.nativeElement.select();

        this.focusTime = new Date().getTime();
    }

    onClick(): void {

        if (this.disabled) return;

        const nowTime = new Date().getTime();
        if (BrowserDetectUtil.isSafari() && (nowTime - this.focusTime) < 300) this.inputElement.nativeElement.select();
    }

    onKeyPress(ev: any): boolean {

        if (this.disabled) return false;

        // Allow copy/paste for safari
        if (ev.ctrlKey || ev.metaKey) return true;

        // Ignore service keys like "Del"
        if (ev.key && !keyPressServiceKeys.contains(ev.key)) {
            // Phone format
            if (this.isPhone) {
                return this.regexPhone.test(ev.key);
            }
            if (this.isNumber) {
                return this.regexNumber.test(ev.key);
            }
            // Number format
            else if (this.valueType === 'number') {
                if (ev.key === '.') {
                    // Only one '.' is allowed
                    return this.precision !== 0 && ev.target.value.indexOf('.') === -1;
                }
                else {
                    const v: string = ev.target.value;
                    const dotIdx = v.indexOf('.');
                    const input = this.inputElement.nativeElement;
                    const checkPrecision = this.precision > 0 && dotIdx !== -1;
                    const hasNoSelection = input.selectionStart === input.selectionEnd;
                    const isFloatInput = (input.selectionStart !== null) && (dotIdx + 1 <= input.selectionStart);

                    // If decimal part more than precision and no selection in input
                    if (checkPrecision && hasNoSelection && isFloatInput && v.substr(dotIdx + 1).length + 1 > this.precision) {
                        return false;
                    }
                    else {
                        return this.regexKeyPress.test(ev.key);
                    }
                }
            }
        }

        return true;
    }

    handleBlur(event: FocusEvent): void {
        if (this.valueType === 'number') {

            this.value = parseFloat(this.value).toString();

            // Empty number value replace with 0
            if (!this.value || Utils.isEmpty('' + this.value) || isNaN(+this.value)) {
                if (this.minValue > 0)
                    this.value = '' + this.minValue;
                else
                    this.value = '0';
            }
            // Check number precision & min value
            else if ((this.precision >= 0) && ('' + this.value).indexOf('.') !== -1)
                this.value = (+this.value).toFixed(this.precision).replace(/\.([^0]+)0+$/, ".$1");
        }
        this.isEditing = false;
    }

    // Focus into cover input div
    handleInputCoverFocus() {
        this.startEdit();
    }

    // Click on cover input div
    handleInputCoverClick() {
        this.startEdit();
    }

    private startEdit() {
        if (this.disabled) return;

        this.isEditing = true;

        setTimeout(() => {
            this.inputElement.nativeElement.focus();
            this.inputElement.nativeElement.select();
        }, 0);
    }

    public clickCover(): void {
        (this.inputCover.nativeElement as HTMLInputElement).click();
    }

    setDisabled(disabled: boolean): void {

        this.disabled = disabled;
        if (this.disabled) {
            //this.renderer.setStyle(this.getEl(), 'opacity', '0.3');
            this.renderer.setStyle(this.getEl(), 'pointer-events', 'none');
            if (this.inputAreaElement && this.inputAreaElement.nativeElement) {
                this.renderer.setStyle(this.inputAreaElement.nativeElement, 'opacity', '0.4');
                this.renderer.addClass(this.inputAreaElement.nativeElement, 'disabled-border-color');
                this.renderer.removeStyle(this.inputAreaElement.nativeElement, 'background-color');
                this.renderer.addClass(this.inputAreaElement.nativeElement, 'inputArea-inactive');
            }
            if (this.topLabel && this.topLabel.nativeElement)
                this.renderer.setStyle(this.topLabel.nativeElement, 'opacity', '0.3');
        }
        else {
            this.renderer.setStyle(this.getEl(), 'opacity', '');
            this.renderer.setStyle(this.getEl(), 'pointer-events', '');

            if (this.inputAreaElement && this.inputAreaElement.nativeElement) {
                this.renderer.removeClass(this.inputAreaElement.nativeElement, 'inputArea-inactive');
                this.renderer.setStyle(this.inputAreaElement.nativeElement, 'opacity', '');
                this.renderer.removeClass(this.inputAreaElement.nativeElement, 'disabled-border-color');
                this.renderer.setStyle(this.inputAreaElement.nativeElement, 'background-color', this.backgroundColor);
            }

            if (this.topLabel && this.topLabel.nativeElement)
                this.renderer.setStyle(this.topLabel.nativeElement, 'opacity', '');
        }
    }

    get value(): string { return this._value; };

    doChecks: boolean = true;
    firstLoad: boolean = true;
    set value(v: string) {
        v = `${v}`; // Convert to string

        if (this.valueType === 'number') {
            // Empty number value replace with 0
            let isNull = false;
            if (!Utils.isValue(v)) {
                v = '0';
                isNull = true;
            }

            if (!this.regex.test(v)) {
                this._value = v;
                this.chDet.detectChanges();
                v = this.prevValue;
            }
            else {
                if (!isNull) {
                    if (this.doChecks || this.firstLoad) {
                        // Revert old value if smaller than minimum or if maximum is exceeded
                        if ((this.minValue && this.minValue > parseFloat(v)) ||
                            (this.maxValue < parseFloat(v))) {
                            const oldValue = this._value;

                            window.setTimeout(() => {
                                this.value = oldValue;
                                this.inputEv.emit(this.value);
                            }, 0);
                        }
                        // check precision
                        else if (this.precision >= 0 && v !== this._value) {
                            const ind = v.indexOf('.');

                            // If value contains dot
                            if (ind !== -1) {
                                let precisionValue: string = '';

                                if (this.precision === 0) {
                                    precisionValue = v.substring(0, ind); // 12.34 => 12
                                }
                                else if (v.substr(v.indexOf('.') + 1).length > this.precision) {
                                    precisionValue = v.substring(0, ind + this.precision + 1); // 12.34567 => 12.34
                                }

                                if (precisionValue.length)
                                    window.setTimeout(() => {
                                        this.value = precisionValue;
                                        this.inputEv.emit(this.value);
                                    }, 0);
                            }
                        }
                        this.firstLoad = false;
                    }
                }
            }
        }
        // Replace null value with empty string
        else if (!Utils.isValue(v)) {
            v = '';
        }

        if (v !== this._value) {
            this._value = v;
            this.handleChange();
            this.onChange(v);

            if (this.c) this.updateError(this.c);
        }

        this.prevValue = this._value;
    }

    public getValue(): string {

        return this.value;
    }

    public setValue(val: string): void {

        this.value = val;
    }

    writeValue(value: any) {
        this.doChecks = false;
        this.value = value;
        this.doChecks = true;
    }

    validate(c: FormControl<string>) {

        this.c = c;
    }

    onChange = (_: any) => { };
    onTouched = () => { };
    registerOnChange(fn: (_: any) => void): void { this.onChange = fn; }
    registerOnTouched(fn: () => void): void { this.onTouched = fn; }
}
